import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { 
  ZoomIn, 
  Check, 
  X, 
  User, 
  Calendar,
  MessageSquare 
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface PhotoReviewData {
  id: string;
  practitioner_id: string;
  photo_url: string;
  photo_description: string | null;
  display_order: number;
  is_active: boolean;
  photo_type: string;
  admin_review_status: string;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  practitioner?: {
    first_name: string | null;
    last_name: string | null;
    professional_title: string | null;
  };
}

interface PhotoReviewCardProps {
  photo: PhotoReviewData;
  onStatusUpdate: () => void;
}

const PhotoReviewCard: React.FC<PhotoReviewCardProps> = ({ 
  photo, 
  onStatusUpdate 
}) => {
  const { toast } = useToast();
  const [isReviewing, setIsReviewing] = useState(false);
  const [adminNotes, setAdminNotes] = useState(photo.admin_notes || '');
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending Review</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleReviewPhoto = async (status: 'approved' | 'rejected') => {
    if (isReviewing) return;
    
    setIsReviewing(true);
    
    try {
      const { error } = await supabase
        .from('practitioner_photos')
        .update({
          admin_review_status: status,
          admin_notes: adminNotes || null,
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', photo.id);

      if (error) throw error;

      toast({
        title: "Photo Review Updated",
        description: `Photo has been ${status === 'approved' ? 'approved' : 'rejected'} successfully.`,
        variant: status === 'approved' ? 'default' : 'destructive'
      });

      onStatusUpdate();
    } catch (error) {
      console.error('Error updating photo review status:', error);
      toast({
        title: "Error",
        description: "Failed to update photo review status. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsReviewing(false);
    }
  };

  const practitionerName = photo.practitioner 
    ? `${photo.practitioner.first_name || ''} ${photo.practitioner.last_name || ''}`.trim() || 'Unknown Practitioner'
    : 'Unknown Practitioner';

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              {practitionerName}
            </div>
          </CardTitle>
          {getStatusBadge(photo.admin_review_status)}
        </div>
        <div className="text-xs text-muted-foreground">
          {photo.practitioner?.professional_title}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Photo Preview */}
        <div className="relative aspect-square overflow-hidden rounded-lg bg-muted">
          {imageLoading && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
          {imageError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-muted">
              <div className="text-center text-muted-foreground">
                <X className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">Failed to load image</p>
              </div>
            </div>
          ) : (
            <img
              src={photo.photo_url}
              alt={photo.photo_description || 'Practitioner photo'}
              className="w-full h-full object-cover transition-opacity duration-200"
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
            />
          )}
          
          {/* Zoom Button */}
          <Dialog>
            <DialogTrigger asChild>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2 h-8 w-8 p-0"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Photo Preview - {practitionerName}</DialogTitle>
              </DialogHeader>
              <div className="relative">
                <img
                  src={photo.photo_url}
                  alt={photo.photo_description || 'Practitioner photo'}
                  className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Photo Description */}
        {photo.photo_description && (
          <div className="text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>{photo.photo_description}</p>
            </div>
          </div>
        )}

        {/* Photo Details */}
        <div className="space-y-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Calendar className="h-3 w-3" />
            Uploaded: {new Date(photo.created_at).toLocaleDateString()}
          </div>
          <div>Type: {photo.photo_type}</div>
          <div>Order: {photo.display_order}</div>
        </div>

        {/* Admin Notes */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Admin Notes</label>
          <Textarea
            placeholder="Add review notes..."
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            className="min-h-[60px] text-sm"
          />
        </div>

        {/* Action Buttons */}
        {photo.admin_review_status === 'pending' && (
          <div className="flex gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  size="sm" 
                  className="flex-1"
                  disabled={isReviewing}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Approve Photo</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to approve this photo? It will be visible to the public.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleReviewPhoto('approved')}>
                    Approve Photo
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  size="sm" 
                  variant="destructive" 
                  className="flex-1"
                  disabled={isReviewing}
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reject Photo</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to reject this photo? The practitioner will be notified.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => handleReviewPhoto('rejected')}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Reject Photo
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {/* Show previous review info if exists */}
        {photo.admin_review_status !== 'pending' && photo.reviewed_at && (
          <div className="text-xs text-muted-foreground border-t pt-2">
            Reviewed on {new Date(photo.reviewed_at).toLocaleString()}
            {photo.admin_notes && (
              <div className="mt-1">
                <strong>Notes:</strong> {photo.admin_notes}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PhotoReviewCard;